// Sources: libms ui/Modal.tsx (controlled open, icon+subtitle header, width, scroll containment) + exams Modal skin (border, rounded-box); rewritten to the daisyUI v5 native <dialog class="modal"> pattern (showModal()/close(), modal-box + modal-action, form method="dialog" backdrop) with aria-labelledby + Esc via native cancel.
import { Show, createEffect, createUniqueId, type Component, type JSX } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { X } from 'lucide-solid';
import { IconButton } from '../icon-button/index.js';

export interface ModalProps {
  /**
   * Controlled visibility. The caller owns it — the dialog never opens or
   * closes itself; native Esc and the backdrop click call `onClose` and wait
   * for `open` to change. Keeping it controlled is what lets a caller refuse
   * to close mid-save.
   */
  open: boolean;
  /**
   * Fired by the header ✕, the backdrop and Esc (the native `cancel`). Set
   * `open` to false here; to block closing (a write in flight) simply do not.
   */
  onClose: () => void;
  /** The dialog's accessible name (`aria-labelledby` points at it) — a short noun phrase, sentence case. */
  title: string;
  /** One line under the title: the record or consequence the decision is about. */
  subtitle?: string;
  /**
   * A lucide-solid component (`icon={Trash2}`) rendered in a tinted capsule
   * beside the title. Pass the COMPONENT, not an element: the dialog owns the
   * 16px size and the `tone` colour.
   */
  icon?: Component<{ size?: number }>;
  /**
   * Header-icon tone. Default `primary` — a neutral dialogue. `warning` is for
   * a DECISION the operator can still take (a policy gate they may override);
   * `error` for a refusal they cannot. The tint is the fastest signal of which
   * of those a dialog is, so it is a prop rather than per-caller markup.
   */
  tone?: 'primary' | 'warning' | 'error' | 'success';
  /** Panel max-width in px (default 480). Widen only for a comparison the operator must see side by side; a form belongs in SidePanel. */
  width?: number;
  /** The body. It scrolls inside the box, so the header and footer stay put. */
  children: JSX.Element;
  /**
   * A ref onto the SCROLLING BODY element — the `overflow-y-auto` box the
   * children sit in. Only the dialog knows which element that is, and a child
   * that watches its own visibility needs it: `@erp/ui/infinite-list`'s
   * sentinel takes it as `root`, or the whole prefetch margin is clipped away
   * by this box and the next page only starts once the reader is already at
   * the bottom. Ordinary dialogs ignore it.
   */
  bodyRef?: (el: HTMLDivElement) => void;
  /**
   * The action row, right-aligned on a base-200 band: Cancel (secondary) then
   * the one primary verb. Omit it for a purely informative dialog closed by ✕.
   */
  footer?: JSX.Element;
  /** Automation id on the `<dialog>`; also seeds the title id (`${id}-title`). */
  id?: string;
}

/**
 * A CONFIRMATION surface only (product decision 2026-08-22): "are you sure?",
 * a policy gate, a refusal. Anything that holds a form, a record or a document
 * opens in `@erp/ui/side-panel` instead, where it is URL-addressable. Native
 * `<dialog>`: focus is trapped, Esc cancels, the backdrop closes, and on
 * close focus returns to the element that opened it. Initial focus: an
 * `autofocus` element, else the body's first field, else the header ✕.
 */
export function Modal(props: ModalProps) {
  const uid = createUniqueId();
  const titleId = () => `${props.id ?? uid}-title`;
  let dialog: HTMLDialogElement | undefined;

  // INITIAL FOCUS (2026-09-20, the keyboard tour): `showModal()` puts focus on
  // the first focusable in DOM order — the header ✕ — unless something inside
  // carries `autofocus`. The rule is: an element marked autofocus wins
  // (ConfirmDialog marks Cancel under `danger`), else the FIRST FIELD in the
  // body (a dialog that asks for a value should open ready to type), else the
  // ✕ as the browser leaves it. Done a microtask after showModal so the
  // native focusing steps have run; harmless where they already chose the
  // same element. Focus RETURN is native: `close()` hands focus back to the
  // element that was focused before showModal (the trigger).
  let body: HTMLDivElement | undefined;
  const placeFocus = (el: HTMLDialogElement) => {
    const marked = el.querySelector<HTMLElement>('[autofocus]');
    const field = body?.querySelector<HTMLElement>('input:not([type="hidden"]), select, textarea');
    (marked ?? field)?.focus();
  };

  createEffect(() => {
    const el = dialog;
    if (!el) return;
    if (props.open && !el.open) {
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', ''); // jsdom / older engines fallback
      queueMicrotask(() => placeFocus(el));
    } else if (!props.open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
  });

  // Native close (Esc cancel, backdrop form method="dialog") → notify the controller.
  const handleClose = () => {
    if (props.open) props.onClose();
  };

  return (
    <dialog ref={dialog} id={props.id} class="modal" aria-labelledby={titleId()} onClose={handleClose}>
      <div
        class="modal-box w-full p-0 flex flex-col overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-raised max-h-[90vh]"
        style={`max-width: ${props.width ?? 480}px;`}
      >
        {/* Header */}
        <div class="flex items-center gap-3 px-5 py-3.5 border-b border-base-300 shrink-0">
          <Show when={props.icon}>
            {/* The tone chip is ICON duty (DEV-GUIDE-002 §12.4, D4): the hue
                rides the aria-hidden chip, the heading beside it is the text. */}
            <div
              class="w-8 h-8 flex items-center justify-center shrink-0 rounded-field"
              classList={{
                'bg-primary-tint text-primary': (props.tone ?? 'primary') === 'primary',
                'bg-warning-tint text-warning': props.tone === 'warning',
                'bg-error-tint text-error': props.tone === 'error',
                'bg-success-tint text-success': props.tone === 'success',
              }}
              aria-hidden="true"
            >
              <Dynamic component={props.icon!} size={16} />
            </div>
          </Show>
          <div class="flex-1 min-w-0">
            <h3 id={titleId()} class="text-sm leading-tight">
              {props.title}
            </h3>
            <Show when={props.subtitle}>
              <p class="text-xs text-base-subtle mt-0.5">{props.subtitle}</p>
            </Show>
          </div>
          {/* The ONE icon-only button (@erp/ui/icon-button): the 32px hit
              floor (40 at touch) around Spectrum's 18px header glyph, a
              rounded square — never the circle this used to hand-roll. No
              tooltip: Adobe's CloseButton has none, and the X is universal. */}
          <IconButton icon={X} label="Close" size="m" tooltip={false} onClick={() => props.onClose()} />
        </div>

        {/* Body — scroll containment stays inside the box */}
        <div
          ref={(el) => {
            body = el;
            props.bodyRef?.(el);
          }}
          class="p-5 overflow-y-auto flex-1"
        >
          {props.children}
        </div>

        {/* Footer */}
        <Show when={props.footer}>
          <div class="modal-action mt-0 flex items-center justify-end gap-2 px-5 py-3.5 border-t border-base-300 bg-base-200 shrink-0">
            {props.footer}
          </div>
        </Show>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button aria-label="Close">close</button>
      </form>
    </dialog>
  );
}
